import { useState, useMemo } from "react";
import { cn } from "../lib/utils";

export interface DataGridColumn<T = Record<string, unknown>> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string | number;
  render?: (value: unknown, row: T) => React.ReactNode;
}

interface DataGridProps<T extends Record<string, unknown> = Record<string, unknown>> {
  columns: DataGridColumn<T>[];
  data: T[];
  pageSize?: number;
  searchable?: boolean;
  striped?: boolean;
  className?: string;
  emptyText?: string;
}

type SortDir = "asc" | "desc";

export function DataGrid<T extends Record<string, unknown>>({
  columns,
  data,
  pageSize = 5,
  searchable = false,
  striped = false,
  className,
  emptyText = "No data",
}: DataGridProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(row =>
      columns.some(col => {
        const v = row[col.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [data, search, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = av == null ? -1 : bv == null ? 1 : av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageData = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  }

  return (
    <div className={cn("dg-root", className)}>
      {searchable && (
        <div className="dg-toolbar">
          <input
            className="dg-search input"
            placeholder="Search…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            aria-label="Search table"
          />
        </div>
      )}

      <div className="dg-table-wrap">
        <table className={cn("dg-table", striped && "dg-striped")}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={cn("dg-th", col.sortable && "dg-th-sortable")}
                  style={col.width != null ? { width: col.width } : undefined}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  aria-sort={
                    sortKey === col.key
                      ? sortDir === "asc" ? "ascending" : "descending"
                      : "none"
                  }
                >
                  <span className="dg-th-inner">
                    {col.header}
                    {col.sortable && (
                      <span className="dg-sort-icon" aria-hidden="true">
                        {sortKey === col.key
                          ? sortDir === "asc" ? " ↑" : " ↓"
                          : " ↕"}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="dg-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              pageData.map((row, ri) => (
                <tr key={ri} className="dg-tr">
                  {columns.map(col => (
                    <td key={col.key} className="dg-td">
                      {col.render
                        ? col.render(row[col.key], row)
                        : String(row[col.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="dg-footer">
          <span className="dg-count">
            {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)} of {sorted.length}
          </span>
          <div className="dg-pages">
            <button
              className="dg-page-btn"
              disabled={safePage === 1}
              onClick={() => setPage(p => p - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
              .reduce<(number | "...")[]>((acc, p, i, arr) => {
                if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "..." ? (
                  <span key={`e${i}`} className="dg-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={cn("dg-page-btn", safePage === p && "dg-page-btn-active")}
                    onClick={() => setPage(p as number)}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              className="dg-page-btn"
              disabled={safePage === totalPages}
              onClick={() => setPage(p => p + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
